import datacentral from '@/utils/axios/datacentral'
import { ResponseArray, ResponseObject } from '@/types/shared'
import {
    Resource,
    Scope,
    Permission,
    Policy,
    PolicyDetail,
    GroupEmployee,
    KeycloakGroupOption,
} from '../types'

// Keycloak base path for authorization management
const KEYCLOAK_BASE = '/auth/keycloak'

// Keycloak list query params
export interface KeycloakListParams {
    name?: string
    first?: number
    max?: number
}

// Resources API — supports name, uri, type, scope filters
export const getResources = async (
    params?: KeycloakListParams
): Promise<ResponseArray<Resource>> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/resources`, {
        params: { ...params, deep: true },
    })
    return response.data
}

// Scopes API — supports name filter
export const getScopes = async (
    params?: KeycloakListParams
): Promise<ResponseArray<Scope>> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/scopes`, {
        params,
    })
    return response.data
}

// Permissions API — supports name, resource, scope, type filters
export const getPermissions = async (
    params?: KeycloakListParams
): Promise<ResponseArray<Permission>> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/permissions`, {
        params,
    })
    return response.data
}

// Policies API — supports name, type filters
export const getPolicies = async (
    params?: KeycloakListParams
): Promise<ResponseArray<Policy>> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/policies`, {
        params,
    })
    return response.data
}

// Policy detail - get roles/users/groups of a policy
export const getPolicyDetail = async (
    id: string
): Promise<ResponseObject<PolicyDetail>> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/policies/${id}`)
    return response.data
}

// Permission associations - get policies/resources/scopes of a permission
export const getPermissionPolicies = async (
    id: string
): Promise<ResponseArray<PolicyDetail>> => {
    const response = await datacentral.get(
        `${KEYCLOAK_BASE}/permissions/${id}/policies`
    )
    return response.data
}

export const getPermissionResources = async (
    id: string
): Promise<ResponseArray<Resource>> => {
    const response = await datacentral.get(
        `${KEYCLOAK_BASE}/permissions/${id}/resources`
    )
    return response.data
}

export const getPermissionScopes = async (
    id: string
): Promise<ResponseArray<Scope>> => {
    const response = await datacentral.get(
        `${KEYCLOAK_BASE}/permissions/${id}/scopes`
    )
    return response.data
}

// Group employees — get Keycloak group members matched with employees
export const getGroupEmployees = async (
    groupId: string
): Promise<ResponseArray<GroupEmployee>> => {
    const response = await datacentral.get(
        `${KEYCLOAK_BASE}/groups/${groupId}/employees`
    )
    return response.data
}

export const getKeycloakGroups = async (): Promise<
    ResponseArray<KeycloakGroupOption>
> => {
    const response = await datacentral.get(`${KEYCLOAK_BASE}/groups`)
    return response.data
}

export const updateDepartmentDefaultGroups = async (
    departmentId: number,
    groupPaths: string[]
) => {
    const response = await datacentral.put(
        `${KEYCLOAK_BASE}/default-group-mappings/departments/${departmentId}`,
        { group_paths: groupPaths }
    )
    return response.data
}

export const updatePositionDefaultGroups = async (
    positionId: number,
    groupPaths: string[]
) => {
    const response = await datacentral.put(
        `${KEYCLOAK_BASE}/default-group-mappings/positions/${positionId}`,
        { group_paths: groupPaths }
    )
    return response.data
}
